import {
  Alert,
  Badge,
  Card,
  cn,
  SimpleTooltip,
  Skeleton,
} from 'corso-admin-ui';
import { formatter } from 'corso-web-lib';
import { useShippingClaimInventoryLevelsQuery } from '~/features/shippingClaimReview/hooks/useShippingClaimInventoryLevelsQuery';
import { useShippingClaim } from '~/features/shippingClaimReview/ShippingClaimReviewProvider';
import { useIsCorsoCxAgent } from '~/hooks/useIsCorsoAdmin';

type ShippingClaimLineItem = ReturnType<
  typeof useShippingClaim
>['shippingClaimLineItems'][number];

export function LineItemInventoryDetail({
  lineItem,
}: {
  lineItem: ShippingClaimLineItem;
}) {
  const isCorsoCxAgent = useIsCorsoCxAgent();

  const { id: shippingClaimId } = useShippingClaim();
  const { data, isLoading } =
    useShippingClaimInventoryLevelsQuery(shippingClaimId);

  const inventory = data?.find(
    (level) => level.kind === lineItem.kind && level.id === lineItem.id,
  );

  // the platform read failed, so `inventoryExists` and `locations` say nothing useful
  const hasInventory = inventory?.inventoryExists ?? false;

  const locations = [...(inventory?.locations ?? [])].sort(
    (a, b) => b.quantity - a.quantity,
  );

  if (!isCorsoCxAgent) return null;

  if (isLoading) return <Skeleton.Rectangle height="20px" width="120px" />;

  return (
    <SimpleTooltip
      content={
        <div className="flex flex-col gap-3 py-2">
          {!inventory && (
            <Alert
              variant="warning"
              heading="No Inventory Information Available"
            />
          )}

          {inventory && (
            <>
              <div className="flex items-center gap-2">
                <span
                  aria-hidden
                  className={cn(
                    'size-2 rounded-full',
                    hasInventory ? 'bg-emerald-600' : 'bg-red-600',
                  )}
                />
                <Card.Heading>
                  {formatter.count(inventory.quantityNeeded)} needed
                </Card.Heading>
              </div>

              {locations.length === 0 ? (
                <Card.Description>
                  Not stocked at any location enabled for reorders.
                </Card.Description>
              ) : (
                <dl className="flex flex-col gap-2 text-sm tabular-nums">
                  {locations.map(({ locationName, quantity, isAvailable }) => (
                    <div key={locationName} className="flex items-center gap-2">
                      <dt className="flex-1 text-neutral-500">
                        {locationName}
                      </dt>
                      {!isAvailable && (
                        <Badge variant="danger">Unavailable</Badge>
                      )}
                      <dd
                        className={cn(
                          'font-medium',
                          isAvailable ? 'text-neutral-900' : 'text-red-700',
                        )}
                      >
                        {formatter.count(quantity)}
                      </dd>
                    </div>
                  ))}
                </dl>
              )}
            </>
          )}

          <div className="border-t border-neutral-100 pt-3">
            <Card.Description>
              Only locations enabled for Reorder are shown.
            </Card.Description>
          </div>
        </div>
      }
    >
      <Badge variant={hasInventory ? 'success' : 'danger'}>
        {hasInventory ? 'Inventory available' : 'Verify inventory'}
      </Badge>
    </SimpleTooltip>
  );
}
